import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { cn } from '~/lib/cn';
import { MediaPlanBreadcrumbs } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/components/MediaPlanBreadcrumbs';
import type {
  DigitalMediaPlan,
  MediaPlanForm,
} from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import type { ArtistDetails } from '~/modules/artist-roster/types';

import { BenchmarksTab } from './components/BenchmarksTab';
import { BudgetDistribution } from './components/BudgetDistribution/BudgetDistribution';
import { MediaPlanHeader } from './components/MediaPlanHeader/MediaPlanHeader';
import { PlatformsTab } from './components/PlatformsTab';
import { getMediaPlanSchema } from './schema';

type TabType = 'platforms' | 'benchmarks';

interface MediaPlanProps {
  artistData: ArtistDetails;
  plan?: DigitalMediaPlan;
  setPlan: (plan?: DigitalMediaPlan) => void;
}

export function MediaPlan({ artistData, plan, setPlan }: MediaPlanProps) {
  const { t } = useTranslation('campaign-planner');
  const [activeTab, setActiveTab] = useState<TabType>('platforms');
  const trackSelection = undefined;
  const campaignObjective = 'Awareness, Engagement';
  const campaignType = 'Project release';

  const formMethods = useForm<MediaPlanForm>({
    resolver: zodResolver(getMediaPlanSchema(t)),
    defaultValues: {
      planName: 'Uptown fanfare launch awareness',
      ...plan,
      platforms: [
        {
          mediaPlatform: 'facebook',
          plans: [
            {
              market: 'DE',
              goal: 'videoViews',
              budget: 10000,
              audienceSize: 10000,
              frequency: { times: 3, period: 'week' },
            },
          ],
        },
        {
          mediaPlatform: 'tiktok',
          plans: [
            {
              market: 'DE',
              goal: 'demandGen',
              budget: 10000,
              audienceSize: 10000,
              frequency: { times: 3, period: 'week' },
            },
          ],
        },
      ],
    },
  });

  const planName = useWatch({ control: formMethods.control, name: 'planName' });

  const tabClassNames = cn(
    'border-b-2xs pb-xs font-semibold duration-300 ease-out border-transparent text-subtle hover:text-default',
  );
  const activeTabClassNames = cn('border-b-primary-default text-default');

  return (
    <div className="px-xl py-lg">
      <FormProvider {...formMethods}>
        <MediaPlanBreadcrumbs artistData={artistData} setPlan={setPlan}>
          {planName}
        </MediaPlanBreadcrumbs>
        <form className="flex h-full flex-col overflow-y-auto overscroll-contain bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat bg-level-01/full">
          <h1 className="mb-lg text-headline-md font-semibold">
            {t('digitalCampaigns.mediaPlan.title')}
          </h1>
          <div>
            <MediaPlanHeader
              trackSelection={trackSelection}
              campaignObjective={campaignObjective}
              campaignType={campaignType}
            />

            <BudgetDistribution />

            {/* Tabs */}
            <div>
              <div
                className="mb-md flex gap-lg border-b text-body-sm border-emphasis-subtle"
                role="tablist"
                aria-label={t('digitalCampaigns.mediaPlan.tabs.label')}
              >
                <button
                  role="tab"
                  type="button"
                  aria-selected={activeTab === 'platforms'}
                  onClick={() => setActiveTab('platforms')}
                  className={cn(tabClassNames, {
                    [activeTabClassNames]: activeTab === 'platforms',
                  })}
                >
                  {t('digitalCampaigns.mediaPlan.tabs.platforms.label')}
                </button>
                <button
                  role="tab"
                  type="button"
                  aria-selected={activeTab === 'benchmarks'}
                  onClick={() => setActiveTab('benchmarks')}
                  className={cn(tabClassNames, {
                    [activeTabClassNames]: activeTab === 'benchmarks',
                  })}
                >
                  {t('digitalCampaigns.mediaPlan.tabs.benchmarks.label')}
                  <Tooltip
                    content={t(
                      'digitalCampaigns.mediaPlan.tabs.benchmarks.tooltip',
                    )}
                    triggerAsChild={true}
                    delayDuration={300}
                    sideOffset={12}
                  >
                    <span className="inline-block pl-xs align-baseline">
                      <Icon type="circle-info" variant="light" size="xs" />
                    </span>
                  </Tooltip>
                </button>
              </div>
              {activeTab === 'platforms' ? <PlatformsTab /> : null}
              {activeTab === 'benchmarks' ? <BenchmarksTab /> : null}
            </div>
          </div>
        </form>
      </FormProvider>
      <DevTool control={formMethods.control} />
    </div>
  );
}

MediaPlan.displayName = 'MediaPlan';
